import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectFilterCustom } from "../../../components/_commons/SelectFilterCustom";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import {
	fetchBizPlatforms,
	fetchBrands,
	fetchCities,
	fetchStores,
	fetchStoresDebounced
} from "../../../actions/actions";
import { SelectFilter } from "../../../components/_commons/SelectFilter";
import { extractInitials, lS, trackEvent } from "../../../atlas-utils";
import { SearchFilter } from "../../../components/_commons/SearchFilter";
import ActivityLogTable from "./ActivityLogTable";
import { Topbar } from "../../../components/_commons/Topbar";
import {
	CATALOGUE_PLATFORMS_LOGO,
	periscopeTimeStatus,
	periscopeTimeStatusSelectOptions
} from "../../../client-config";
import {
	fetchActivityLogItemDetails,
	fetchActivityLogLocationDetails,
	fetchDebouncedActivityLogItemDetails,
	fetchDebouncedActivityLogLocationDetails,
	fetchPeriscopeEnabledBrands,
	fetchPeriscopeEnabledLocations,
	fetchStores as fetchStoresList
} from "../../../actions/periscope";
import { scroll } from "../../../atlas-utils";
import MainHeader from "../../../components/Periscope/MainHeader";
import { ActionTypes } from "../../../actions/_types";
import { getItemsByPage, periscopeFilterEvent } from "../../../helpers/periscope";
import SatismeterService from "../../../services/SatismeterService";
import { SelectFilterCustomGroup } from "../../../components/_commons/SelectFilterCustomGroup";
import uniqBy from "lodash/uniqBy";

const ActivityLog = ({
	configItems,
	periscopeActivityLogState,
	login,
	periscopeDataResponse,
	periscopeFilters,
	periscopeCountryWiseSupportedPlatforms
}) => {
	const country = (login?.loggedInbizDetail?.country || lS.get("auth")?.biz?.country).toLowerCase();
	const bizPlatforms = configItems?.bizPlatforms?.items.filter((platform) => {
		if (platform.platformName == "All Platforms") {
			return true;
		}
		return (periscopeCountryWiseSupportedPlatforms?.[country] ||
			lS.get("periscopeCountryWisePlatforms")?.[country])?.[platform.platformName.toLowerCase()]?.enabled;
	});
	const bizPlatformsLoading = configItems?.bizPlatforms?.isLoading;

	const text = periscopeActivityLogState?.text || "";
	const brands = configItems?.brands?.items;
	const [tab, setTab] = useState("location");
	const tableRef = useRef(null);
	const stores = configItems?.stores?.items;
	const isMultibrandEnabled = login?.loggedInbizDetail?.isMultibrandEnabled;
	const [selectedPlatform, setSelectedPlatform] = useState(periscopeFilters?.selectedPlatform);

	const periscopeBrandIds = periscopeFilters?.brands;
	const periscopeLocationIds = periscopeFilters?.locations;

	const [filteredBrands, setFilteredBrands] = useState([]);
	const [timeOption, setTimeOption] = useState("last_1_hour");

	useEffect(() => {
		if (!isMultibrandEnabled || !brands || !periscopeBrandIds) return;

		const filteredBrands = brands.filter((brand) => {
			return periscopeBrandIds.includes(String(brand?.id)) || brand?.id === "all";
		});
		setFilteredBrands(filteredBrands);
	}, [isMultibrandEnabled, brands, periscopeBrandIds]);

	const setOffsetZero = () => {
		store.dispatch({
			type: ActionTypes.ACTIVITY_LOG_STATE_FILTERS,
			payload: { offset: 0 }
		});
	};
	const handlePlatform = (value) => {
		periscopeFilterEvent("platform");
		setSelectedPlatform(value);
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			payload: {
				selectedPlatform: { id: value.id, platformName: value.platformName }
			}
		});
		setOffsetZero();
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};
	const handlePlatformsLabelOption = (plf) => {
		return (
			<React.Fragment>
				<div className="logo">
					<img
						src={
							CATALOGUE_PLATFORMS_LOGO[plf.platformName.toLowerCase()] ||
							"/assets/icons/icons8-globe-40.png"
						}
						alt=""
					/>
				</div>
				<div className="dropdown-title" title={plf.platformName}>
					{plf.platformName}
				</div>
			</React.Fragment>
		);
	};
	const [selectedBrand, setSelectedBrand] = useState(isMultibrandEnabled ? periscopeFilters.selectedBrand : null);
	const handleBrand = (brand) => {
		periscopeFilterEvent("brand");
		setSelectedBrand(brand);
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			payload: {
				selectedBrand: brand
			}
		});
		setTimeOption("last_1_hour");
		setOffsetZero();
		handleReset();
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};
	const handleReset = () => {
		setLocationSelected({ id: "all", name: "All Locations" });
		setSelectedPlatform({ id: "all", platformName: "All Platforms" });
		setStatus("all");
		setTimeOption("last_1_hour");
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			selectedPlatform: { id: "all", platformName: "All Platforms" }
		});
	};
	const handleBrandsLabelOption = (brand) => {
		return (
			<React.Fragment>
				<div className={"logo " + brand.color}>
					{brand.image ? <img src={brand.image} alt="" /> : extractInitials(brand?.name?.split(" "))}
				</div>
				<div className="dropdown-title" title={brand.name}>
					{brand.name}
				</div>
			</React.Fragment>
		);
	};
	const [locationSelected, setLocationSelected] = useState({ id: "all", name: "All Locations" });

	const handleLocation = useCallback((value) => {
		periscopeFilterEvent("location");
		setLocationSelected(value);
		setOffsetZero();
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	}, []);

	useEffect(() => {
		fetchBizPlatforms(false, false, true);
		fetchBrands("", true);
		fetchStores("", 50, null, true);

		fetchPeriscopeEnabledBrands(String(login?.loggedInbizDetail?.id));
		fetchPeriscopeEnabledLocations(String(login?.loggedInbizDetail?.id));

		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 157, left: 0 });
		}
		SatismeterService.satismeterEvent("periscopeActivityLogAccessed");
	}, []);

	const [status, setStatus] = useState("all");

	const displayStatus = {
		all: "All",
		online: "Online",
		offline: "Offline",
		removed: "Removed"
	};

	const handleStatusFilter = (val) => {
		periscopeFilterEvent("status");
		if (val?.value) {
			setStatus(val.value);
		}
		setOffsetZero();
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};

	const handleTimeFilter = (val) => {
		if (val?.value) {
			setTimeOption(val.value);
		}
		setOffsetZero();
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};

	const [searchQuery, setSearchQuery] = useState("");

	const handleSearch = (field, value) => {
		setSearchQuery(value);
		store.dispatch({
			type: ActionTypes.ACTIVITY_LOG_STATE_FILTERS,
			payload: { text: value, offset: 0 }
		});
		textDebouncedFetch(value);
	};

	const limit = periscopeActivityLogState?.limit;
	const offset = periscopeActivityLogState?.offset;
	const buildVariables = () => {
		let platformSelected =
			selectedPlatform?.platformName == "All Platforms" ? "all" : selectedPlatform?.platformName?.toLowerCase();
		return {
			bizId: String(login?.loggedInbizDetail?.id),
			brand: isMultibrandEnabled ? String(selectedBrand?.id || "all") : null,
			location: String(locationSelected?.id),
			status: status,
			time: timeOption,
			platform: platformSelected,
			limit: limit,
			offset: offset,
			search: searchQuery
		};
	};

	const fetchData = (periscopeDataFetch = false) => {
		if (tab == "location") {
			fetchActivityLogLocationDetails(buildVariables(), isMultibrandEnabled, periscopeDataFetch);
		} else if (tab == "item") {
			fetchActivityLogItemDetails(buildVariables(), isMultibrandEnabled, periscopeDataFetch);
		}
	};

	useEffect(() => {
		fetchData();
	}, [
		selectedBrand?.id,
		locationSelected?.id,
		status,
		timeOption,
		selectedPlatform?.platformName,
		limit,
		offset,
		tab
	]);
	const periscopeDataFetch = periscopeDataResponse?.periscopeDataFetch;
	useEffect(() => {
		if (periscopeDataFetch) {
			fetchData(periscopeDataFetch);
		}
	}, [periscopeDataFetch]);

	const textDebouncedFetch = (textValue) => {
		let variables = buildVariables();
		variables = { ...variables, search: textValue };
		if (tab == "location") {
			fetchDebouncedActivityLogLocationDetails(variables, isMultibrandEnabled);
		} else if (tab == "item") {
			fetchDebouncedActivityLogItemDetails(variables, isMultibrandEnabled);
		}
	};

	const FORM_TABS = [
		{
			label: "Location Availability",
			value: "location"
		},
		{
			label: "Item Availability",
			value: "item"
		}
	];

	const handleFormTab = (tab) => {
		trackEvent("periscope_detail_view_clicked", { module: `activity_log_${tab.value}` });
		setTab(tab.value);
		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 157, left: 0 });
		}
		setOffsetZero();
	};

	const handleFilterState = (isOpen, field) => {
		let filters = {
			locations: () => fetchStoreData(true)
		};
		if (isOpen) {
			const filterAction = filters[field];
			filterAction();
		} else {
			if (field == "locations") {
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: {
							text: ""
						}
					}
				});
			}
		}
	};

	const fetchStoreData = useCallback(
		async (offsetZero = false) => {
			try {
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: { isLoading: true }
					}
				});
				if (!Array.isArray(periscopeLocationIds) || periscopeLocationIds.length === 0) {
					store.dispatch({
						type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
						payload: {
							metric: "locationsList",
							data: { isLoading: false }
						}
					});
					return;
				}

				const limit = periscopeFilters?.locationsList?.limit || 50;
				const currentItems = periscopeFilters?.locationsList?.items || [];
				const offset = offsetZero ? 0 : currentItems.length ? currentItems.length - 1 : 0;

				let ids = getItemsByPage(periscopeLocationIds, offset, limit);

				const respItems = await fetchStoresList("", limit, 0, null, offset == 0 ? true : false, ids);

				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,

					payload: {
						metric: "locationsList",
						data: {
							items:
								offset == 0
									? uniqBy([...(respItems?.data?.stores?.objects || [])], "id")
									: uniqBy(
											[
												...(periscopeFilters?.locationsList?.items || []),
												...(respItems?.data?.stores?.objects || [])
											],
											"id"
										),

							offset: respItems?.data?.stores?.offset || 0,
							limit,
							count: (periscopeLocationIds && periscopeLocationIds.length) || 0,
							isLoading: false
						}
					}
				});
			} catch (err) {
				console.log("Error fetching store list", err);
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: { isLoading: false }
					}
				});
			}
		},
		[periscopeFilters?.locationsList, periscopeLocationIds]
	);
	const handleFetchOption = useCallback(
		(entity, offsetZero = false) => {
			periscopeLocationIds && periscopeLocationIds.length > 0 && fetchStoreData(offsetZero);
		},
		[periscopeLocationIds, fetchStoreData]
	);
	const [locationSearchText, setLocationSearchText] = useState(false); //Boolean
	const handleLocationTextSearch = (text) => {
		if (text) {
			setLocationSearchText(true);
		} else {
			setLocationSearchText(false);
		}
		store.dispatch({
			type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
			payload: {
				metric: "locationsList",
				data: {
					text: text || ""
				}
			}
		});
		fetchStoresDebounced(text, 50, null, false);
	};

	useEffect(() => {
		if ((periscopeLocationIds || []).length) {
			fetchStoreData(true);
		}
	}, [(periscopeLocationIds || []).length, periscopeFilters?.locationsList?.limit]);

	const locationSearchedText = periscopeFilters?.locationsList?.text || "";

	const allLocations = useMemo(() => {
		return (stores || []).filter((store) => {
			if (!Array.isArray(periscopeLocationIds) || periscopeLocationIds.length === 0) {
				return false;
			}

			if (!store) return false;
			const isMatchingName = store?.name
				? store.name.toLowerCase()?.includes((locationSearchedText || "").toLowerCase()) &&
					periscopeLocationIds.includes(String(store.id))
				: false;

			return isMatchingName;
		});
	}, [stores, periscopeLocationIds, locationSearchedText]);

	const renderCustomLabelLocations = useCallback((option) => {
		return <div className="status-heading">{option[0]["currValue"]["name"]}</div>;
	}, []);
	let optionValues = useMemo(() => {
		return [
			{
				options: locationSearchText ? allLocations : periscopeFilters?.locationsList?.items || [],
				isLoading: locationSearchText
					? configItems?.stores?.isLoading
					: periscopeFilters?.locationsList?.isLoading || false,
				showSelectAllOption: true,
				field: "locations",
				labelKey: "name",
				valueKey: "id",
				ellipsizedLength: 27,
				iconKey: "image",
				countDisplayText: "location",
				viewAllBtn: true,
				setOffsetToZero: true,
				viewMore:
					periscopeFilters?.locationsList &&
					periscopeFilters?.locationsList?.items &&
					(periscopeFilters?.locationsList?.items || []).length < periscopeFilters?.locationsList?.count + 1,
				isSearchExternal: true,
				handleFetchViewOptions: handleFetchOption,
				isSearchable: true,
				placeholder: "All Locations",
				setFilter: (f, value) => handleLocation(value),
				currValue: locationSelected
			}
		];
	}, [
		locationSearchText,
		allLocations,
		periscopeFilters?.locationsList,
		configItems?.stores?.isLoading,
		handleFetchOption,
		handleLocation,
		locationSelected
	]);

	return (
		<div className="periscope-container">
			<div
				ref={tableRef}
				className="section-container-common-periscope periscope-activity-log"
				data-testid="activity-log"
			>
				<MainHeader classname={"activity-div-header"} headerText="Activity Log" />
				<div className="filters-activity-log brand-state-filters">
					<div className="left-filters-log">
						{isMultibrandEnabled && (
							<SelectFilterCustom
								options={filteredBrands}
								field="brands"
								currValue={selectedBrand}
								setFilter={(f, value) => handleBrand(value)}
								labelKey={"name"}
								valueKey={"id"}
								isSearchable={true}
								customLabel={true}
								customOptions={true}
								renderLabel={handleBrandsLabelOption}
								renderOptions={handleBrandsLabelOption}
								placeholder="Select brand"
							/>
						)}

						<SelectFilterCustomGroup
							renderCustomLabel={renderCustomLabelLocations}
							customLabel
							groupedOptions={optionValues}
							handleSelectFilterState={handleFilterState}
							handleSearch={handleLocationTextSearch}
						/>

						{bizPlatforms && bizPlatforms.length > 1 && (
							<SelectFilterCustom
								options={bizPlatforms}
								isLoading={bizPlatformsLoading}
								field="platforms"
								currValue={selectedPlatform || null}
								setFilter={(f, value) => handlePlatform(value)}
								labelKey={"platformName"}
								valueKey={"id"}
								isSearchable={true}
								customLabel={true}
								customOptions={true}
								renderLabel={handlePlatformsLabelOption}
								renderOptions={handlePlatformsLabelOption}
								placeholder="Select platform"
							/>
						)}
						<div style={{ width: "160px" }}>
							<SelectFilter
								options={[
									{ value: "all", valueForDisplay: "All" },
									{ value: "offline", valueForDisplay: "Offline" },
									{ value: "online", valueForDisplay: "Online" },
									{ value: "removed", valueForDisplay: "Removed" }
								]}
								field="status"
								currValue={{
									value: status,
									valueForDisplay: (
										<span className="status-main">
											<span className="status-heading">Status</span>
											<span className="status-display">{displayStatus[status] || "All"}</span>
										</span>
									)
								}}
								labelKey="valueForDisplay"
								valueKey="value"
								setFilter={(f, v) => handleStatusFilter(v)}
								isSearchable={false}
								isClearable={false}
							/>
						</div>
						<div style={{ width: "165px" }}>
							<SelectFilter
								options={periscopeTimeStatusSelectOptions}
								field="timeOption"
								currValue={{
									value: timeOption,
									valueForDisplay: (
										<span className="status-main">
											<span className="status-heading">Time</span>
											<span className="status-display">{periscopeTimeStatus[timeOption]}</span>
										</span>
									)
								}}
								labelKey="valueForDisplay"
								valueKey="value"
								setFilter={(f, v) => handleTimeFilter(v)}
								isSearchable={false}
								isClearable={false}
							/>
						</div>
					</div>

					<div>
						<SearchFilter
							placeholder="Search"
							filterOption={{
								field: "name"
							}}
							setFilter={handleSearch}
							value={searchQuery}
						/>
					</div>
				</div>

				<div>
					<Topbar tabs={FORM_TABS} selectedTab={tab} switchTab={(tab) => handleFormTab(tab)} />
				</div>

				<ActivityLogTable
					fetchData={fetchData}
					tableRef={tableRef}
					tableName={tab}
					selectedBrand={selectedBrand}
				/>
			</div>
		</div>
	);
};
const mapStateToProps = (state) => ({
	configItems: state.configItems,
	login: state.login,
	periscopeActivityLogState: state.periscopeActivityLogState,
	periscopeDataResponse: state.periscopeDataResponse,
	periscopeCountryWiseSupportedPlatforms: state.periscopeCountryWiseSupportedPlatforms,
	periscopeFilters: state.periscopeFilters
});
export default connect(mapStateToProps)(ActivityLog);
